import { join, map, size, slice, sumBy } from 'lodash';
import { stringify } from 'qs';

import { PlotlyFigure } from 'interfaces/agentSession';
import { apiRequestHandlerPromise } from 'utils/apiRequestHandler';
import { CasesParams } from 'utils/useCasesParams/types';

export interface SkippedMarkerAssignments {
  slideId: string;
  markerTypesBySourceChannelName: { [sourceChannelName: string]: string };
}

export interface ChannelMarkerTypesUpdateResponse {
  updatedSlideIds: string[];
  // Requested source channel names a slide does not have, so nothing was written for them
  skippedAssignments: SkippedMarkerAssignments[];
}

const MAX_SKIPPED_SLIDES_IN_MESSAGE = 3;

export const describeSkippedMarkerAssignments = (skipped: SkippedMarkerAssignments[]) => {
  if (size(skipped) === 0) {
    return null;
  }

  const skippedAssignmentsCount = sumBy(skipped, ({ markerTypesBySourceChannelName }) =>
    size(markerTypesBySourceChannelName)
  );
  const slideDescriptions = map(
    slice(skipped, 0, MAX_SKIPPED_SLIDES_IN_MESSAGE),
    ({ slideId, markerTypesBySourceChannelName }) =>
      `${slideId} (${join(
        map(markerTypesBySourceChannelName, (markerType, sourceChannelName) => `${sourceChannelName}: ${markerType}`),
        ', '
      )})`
  );
  const remainingSlidesCount = size(skipped) - size(slideDescriptions);

  return `Skipped ${skippedAssignmentsCount} marker ${
    skippedAssignmentsCount === 1 ? 'assignment' : 'assignments'
  } for channels that ${size(skipped)} ${size(skipped) === 1 ? 'slide does' : 'slides do'} not have: ${join(
    slideDescriptions,
    '; '
  )}${remainingSlidesCount > 0 ? `; and ${remainingSlidesCount} more` : ''}`;
};

/** Applies the source slide's marker names to the study by source channel name, since channel order differs across slides. */
export const updateStudyMarkers = (args: {
  studyId: string;
  sourceSlideId: string;
  markerTypes: { [channelId: string]: string };
}) => {
  return apiRequestHandlerPromise<ChannelMarkerTypesUpdateResponse>({
    url: `multiplex_markers/types/study.update/${args.studyId}?${stringify({ sourceSlideId: args.sourceSlideId })}`,
    method: 'PUT',
    data: JSON.stringify(args.markerTypes),
  });
};

export const updateSlideChannelMarkers = (args: { slideId: string; markerTypes: { [channelId: string]: string } }) => {
  return apiRequestHandlerPromise({
    url: `multiplex_markers/types/slide.update/${args.slideId}`,
    method: 'PUT',
    data: JSON.stringify(args.markerTypes),
  });
};

export const updateSlideChannelMarkersQc = (args: { slideId: string; qcMarkers: { [channelId: string]: string } }) => {
  return apiRequestHandlerPromise({
    url: `multiplex_markers/qc/slide.update/${args.slideId}`,
    method: 'PUT',
    data: JSON.stringify(args.qcMarkers),
  });
};

export interface SourceChannelAssignment {
  channelId: string;
  // Distinct channel names recorded in slide metadata for this channel id
  sourceNames: string[];
  assignedMarkerTypes: string[];
  slidesCount: number;
  missingSlideIds: string[];
}

export interface SourceChannelNameAssignment {
  sourceName: string;
  // Channel ids this source name occupies; differs across slides when channel order differs
  channelIds: string[];
  assignedMarkerTypes: string[];
  slidesCount: number;
  missingSlideIds: string[];
}

export interface SourceChannelsResponse {
  channels: SourceChannelAssignment[];
  sourceNameChannels: SourceChannelNameAssignment[];
  totalSlidesCount: number;
  doAllSlidesHaveSameChannels: boolean;
  selectedSlides: { slideId: string; originalFileName?: string | null }[];
  markerCoverage?: Record<string, { slideIds: string[]; bitDepths: (8 | 16)[] }>;
}

export const getSourceChannelsQueryKey = (casesParams: CasesParams) => ['multiplex_source_channels', casesParams];

export const getChannelSourceLabel = (channel: SourceChannelAssignment) =>
  size(channel.sourceNames) > 0 ? join(channel.sourceNames, ' / ') : 'Unnamed channel';

export const getSourceChannels = (casesParams: CasesParams, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<SourceChannelsResponse>({
    url: `multiplex_markers/source_channels.get`,
    method: 'POST',
    signal,
    data: JSON.stringify(casesParams),
  });
};

export const bulkUpdateChannelMarkerTypes = (args: {
  casesParams: CasesParams;
  sourceChannelMarkerTypes: { [sourceChannelName: string]: string };
}) => {
  return apiRequestHandlerPromise<ChannelMarkerTypesUpdateResponse>({
    url: `multiplex_markers/types/bulk.update`,
    method: 'POST',
    data: JSON.stringify({ ...args.casesParams, sourceChannelMarkerTypes: args.sourceChannelMarkerTypes }),
  });
};

export interface GetChannelMarkerTypesResponse {
  channelMarkerTypes: string[];
  channelAppearance: { [channelId: string]: number }; // The number of slides that have this channel
  markersToMissingSlideIds: { [channelId: string]: string[] }; // markers in a cohort that are missing from certain slides
  totalSlidesCount: number;
  doAllSlidesHaveSameChannels: boolean;
  numberOfChannelsToSlideIds?: { [channelNumber: number]: string[] }; // If not doAllSlidesHaveSameChannels, map channel numbers to slide IDs that have them
  slidesWithMissingChannelAssignments: string[];
  channelModalities?: Record<string, 'dna' | 'rna' | 'mif'>;
}

export const getChannelMarkersQueryKey = (casesParams: CasesParams) => ['multiplex_marker_types', casesParams];

export const getChannelMarkers = (casesParams: CasesParams, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<GetChannelMarkerTypesResponse>({
    url: `multiplex_markers.get`,
    method: 'POST',
    signal,
    data: JSON.stringify(casesParams),
  });
};

export interface SlideMarkerQcSummary {
  slideId: string;
  originalFileName?: string;
  channelMarkerTypes?: Record<string, string> | null;
  channelMarkersQc?: Record<string, string> | null;
}

export interface SlideMarkerQcBarcodeResponse {
  plotlyFigure: PlotlyFigure;
  orderedSlideIds: string[];
  orderedSlideLabels: string[];
  markerIds: string[];
  markerLabels: string[];
  slideCount: number;
  markerCount: number;
  labelledCellsCount: number;
}

export const getSlideMarkerQcSummary = (casesParams: CasesParams, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<SlideMarkerQcSummary[]>({
    url: `multiplex_markers/qc_summary.get`,
    method: 'POST',
    signal,
    data: JSON.stringify(casesParams),
  });
};

export const getSlideMarkerQcBarcode = (
  {
    casesParams,
    markerIds,
    cohortMarkerQcSettings,
    colorBy,
  }: {
    casesParams: CasesParams;
    markerIds?: string[];
    cohortMarkerQcSettings?: Record<string, string>;
    colorBy?: 'label' | 'impact';
  },
  signal?: AbortSignal
) => {
  return apiRequestHandlerPromise<SlideMarkerQcBarcodeResponse>({
    url: `multiplex_markers/qc_barcode.get`,
    method: 'POST',
    signal,
    data: JSON.stringify({
      ...casesParams,
      markerIds,
      cohortMarkerQcSettings,
      colorBy,
    }),
  });
};
